import styled, { css } from "styled-components";
import { HashLink as Link } from "react-router-hash-link";
import Container from "../../atoms/Container/Container.styled";
import {
  StyledButton,
  StyledButtonLink,
} from "../../atoms/Button/Button.styled";
import { deviceMax } from "../../../utils/device";

interface INavOpen {
  isOpen?: boolean;
}

interface ICompact {
  $compact?: boolean;
}

export const HeaderWrapper = styled.header<ICompact>(
  ({ theme, $compact }) => css`
    background: ${theme.colors.brand.deep};
    color: ${theme.colors.white};
    position: sticky;
    top: 0;
    transition: background 0.2s ease, box-shadow 0.2s ease;
    z-index: 15;

    &::before {
      background: ${theme.gradients.brand};
      content: "";
      height: 4px;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }

    ${$compact &&
    css`
      background: rgba(44, 62, 80, 0.92);
      backdrop-filter: blur(10px);
      box-shadow: 0 8px 24px rgba(16, 32, 48, 0.22);
    `};
  `
);

export const Nav = styled(Container)<INavOpen & ICompact>(
  ({ isOpen, $compact }) => css`
    align-items: center;
    display: flex;
    gap: 32px;
    height: 106px;
    justify-content: space-between;
    transition: height 0.2s ease;

    @media ${deviceMax.tabletWideMax} {
      height: 90px;
    }

    @media ${deviceMax.phoneMax} {
      gap: 14px;
      height: 89px;
    }

    ${$compact &&
    css`
      height: 68px;

      @media ${deviceMax.tabletWideMax} {
        height: 60px;
      }

      @media ${deviceMax.phoneMax} {
        height: 56px;
      }
    `};

    /* Kept last so the expanded state wins over every breakpoint above. */
    ${isOpen &&
    css`
      @media ${deviceMax.tabletWideMax} {
        flex-wrap: wrap;
        height: auto;
        max-height: calc(100vh - 20px);
        overflow-y: auto;
        padding: 20px 0;
      }
    `};
  `
);

export const BrandLink = styled(Link)`
  text-decoration: none;
`;

export const NavLinks = styled.nav<INavOpen>(
  ({ theme, isOpen }) => css`
    align-items: center;
    display: flex;
    gap: 30px;
    margin-left: auto;

    @media ${deviceMax.desktopSMax} {
      gap: 20px;
    }

    @media ${deviceMax.tabletWideMax} {
      display: ${isOpen ? "flex" : "none"};
      align-items: stretch;
      border-top: 1px solid ${theme.colors.brand.onDark.line};
      flex: 1 0 100%;
      flex-direction: column;
      gap: 0;
      margin-top: 7px;
      order: 3;
    }
  `
);

export const NavLink = styled(Link)(
  ({ theme }) => css`
    color: #e0e6ef;
    font-size: 13px;
    font-weight: ${theme.typography.weights.medium};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.brand.cyan};
    }

    @media ${deviceMax.tabletWideMax} {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      padding: 13px 0;
    }
  `
);

export const NavActions = styled.div<ICompact>(
  ({ $compact }) => css`
    align-items: center;
    display: flex;
    gap: 24px;

    ${StyledButton}, ${StyledButtonLink} {
      font-size: 13px;
      min-height: 43px;
      padding: 10px 17px;
      transition: min-height 0.2s ease, padding 0.2s ease;
    }

    @media ${deviceMax.desktopSMax} {
      gap: 17px;
    }

    @media ${deviceMax.tabletWideMax} {
      margin-left: auto;
    }

    @media ${deviceMax.phoneMax} {
      gap: 10px;

      ${StyledButton}, ${StyledButtonLink} {
        font-size: 11px;
        min-height: 40px;
        padding: 10px 12px;
      }
    }

    @media ${deviceMax.compactMax} {
      gap: 8px;

      ${StyledButton}, ${StyledButtonLink} {
        font-size: 10px;
        padding-left: 10px;
        padding-right: 10px;
      }
    }

    ${$compact &&
    css`
      ${StyledButton}, ${StyledButtonLink} {
        min-height: 38px;
        padding: 8px 15px;
      }
    `};
  `
);

export const MenuToggle = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid #6b7b8e;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: none;
  height: 44px;
  justify-content: center;
  width: 44px;

  @media ${deviceMax.tabletWideMax} {
    display: flex;
  }

  @media ${deviceMax.phoneMax} {
    height: 40px;
    width: 40px;
  }
`;
